/* The pieces every screen shares, drawn from the artboard.
 *
 * Nothing here knows about the API. It takes what the screens already hold and
 * draws it the way `Damga Customer App.dc.html` does.
 */
import { iconSvg } from "@damga/ds";
import { type StoredSecret, currentCode, t } from "@damga/kit";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";

/* The design system's own line icons: same 22-unit grid and 1.6 stroke as the
 * artboard's. Static strings from our own package, so rendering them as markup
 * carries nothing a visitor wrote. */
export function Icon({ name, size = 19 }: { name: string; size?: number }) {
  return <span className="icon" aria-hidden="true" dangerouslySetInnerHTML={{ __html: iconSvg(name, size) }} />;
}

export function Brand() {
  return (
    <div className="brand">
      <span className="mark" aria-hidden="true" />
      <span className="brand-word">Damga</span>
    </div>
  );
}

/* A HUE PER SHOP, from the shop's id, so a card is the same colour on every
 * visit. The artboard gives each card a hue by hand; with real shops there is
 * no hand, and a colour that changed between launches would read as a
 * different card. Four teal-family hues, all from the artboard's own ramp. */
const HUES = ["#0b5c74", "#11525e", "#075985", "#0e7490"];
export function hueFor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return HUES[h % HUES.length];
}

/* THE CODE, ALIVE.
 *
 * Kept from the old code screen, and the reasons still hold: one tick a second
 * is the coarsest that keeps the countdown honest; an interval rather than an
 * animation frame so it keeps working while the screen is dim; and a recompute
 * on wake, because a customer takes the phone out and holds it up at once, and
 * for up to a second the screen would show a code that had already rotated. */
export function useLiveCode(secret: StoredSecret | null) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const wake = () => setNow(Date.now());
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("pageshow", wake);
    window.addEventListener("focus", wake);
    const id = setInterval(wake, 1000);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("pageshow", wake);
      window.removeEventListener("focus", wake);
    };
  }, []);
  if (!secret) return null;
  const live = currentCode(secret, now + (secret.skewMs ?? 0));
  return { ...live, fraction: live.secondsLeft / secret.period_seconds };
}

/* Six cells, three and three, because they are read aloud across a counter and
 * three and three is how people say them.
 *
 * NO ANIMATION ON THE ROLLOVER, which the artboard's scale-in would have been
 * every minute. A customer sees this more than any other thing in the app, and
 * motion on something seen that often reads as latency. The bar is the only
 * moving part, and it is remounted per code window so it never animates
 * backwards from empty to full. */
export function CodeDigits({ secret }: { secret: StoredSecret | null }) {
  const live = useLiveCode(secret);
  if (!live) return <p className="muted">{t("common.loading")}</p>;
  const cells = live.code.split("");
  return (
    <div className="code-block">
      <p className="visually-hidden" aria-live="off">
        {`Your code is ${cells.join(" ")}`}
      </p>
      <div className="digits" aria-hidden="true">
        {cells.map((d, i) => (
          <DigitCell key={i} d={d} gapBefore={i === 3} />
        ))}
      </div>
      <div className="code-meta">
        <span className="timer" aria-hidden="true">
          <span
            key={live.window}
            className="timer-bar"
            style={{ ["--fraction" as string]: String(live.fraction) }}
          />
        </span>
        <span className="timer-text" role="timer">
          {t("code.rotates", "en", { seconds: live.secondsLeft })}
        </span>
      </div>
    </div>
  );
}

function DigitCell({ d, gapBefore }: { d: string; gapBefore: boolean }) {
  return (
    <>
      {gapBefore && <span className="digits-gap" />}
      <span className="digit">{d}</span>
    </>
  );
}

/* A BOTTOM SHEET THAT BEHAVES LIKE A DIALOG.
 *
 * The artboard draws the sheet; these are the parts a drawing cannot show.
 * Focus moves into it and back to whatever opened it. Escape closes it, as a
 * tap on the scrim does. The page behind does not scroll while it is open. */
export function Sheet({
  onClose,
  eyebrow,
  title,
  danger = false,
  children,
}: {
  onClose: () => void;
  eyebrow: string;
  title: string;
  danger?: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // Through a ref, so a parent that passes a new function each render does not
  // re-run this: that would pull focus back to the sheet on every keystroke.
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close.current();
    };
    document.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
      opener?.focus?.();
    };
  }, []);

  return (
    <div className="scrim" onClick={onClose}>
      <div
        ref={ref}
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <span className="grabber" aria-hidden="true" />
        <p className="label" data-accent={danger ? undefined : ""} data-danger={danger ? "" : undefined}>
          {eyebrow}
        </p>
        <h2 className="sheet-title" id={titleId}>
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}

export function SheetFacts({ facts }: { facts: { k: string; v: string }[] }) {
  if (facts.length === 0) return null;
  return (
    <div className="sheet-facts">
      {facts.map((f) => (
        <div className="sheet-fact" key={f.k}>
          <span>{f.k}</span>
          <span>{f.v}</span>
        </div>
      ))}
    </div>
  );
}

/* A toast enters from just below where it rests, and it is announced. The
 * entering state is set for one frame and removed, which is the pattern that
 * works in every browser this app meets; `@starting-style` does not yet. */
export function Toast({ message, stamp }: { message: string; stamp: number }) {
  const [entering, setEntering] = useState(true);
  useEffect(() => {
    setEntering(true);
    // A timer as well as the frame: a page the browser considers hidden runs
    // no animation frames, and a toast that never leaves its entering state is
    // a message nobody sees.
    const done = () => setEntering(false);
    const frame = requestAnimationFrame(() => requestAnimationFrame(done));
    const timer = setTimeout(done, 60);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [stamp]);
  return (
    <div className="toast" role="status" data-entering={entering ? "" : undefined}>
      {message}
    </div>
  );
}

/* The seal, pressed onto the screen when something real happened: a stamp
 * that landed while the app was open, a reward that became claimable, a name
 * saved. Never on a tap that only opened something. */
export function Seal({ label }: { label: string }) {
  return (
    <div className="seal-layer" aria-hidden="true">
      <span className="seal-ring" />
      <span className="seal">
        <b>✓</b>
        <span>{label}</span>
      </span>
    </div>
  );
}

/* THE INTRO, ON THE SIGNED-OUT SCREEN ONLY, once a session.
 *
 * The artboard plays it before everything. A customer opening the app at a
 * counter to show their code would wait two seconds behind it, so it never
 * covers the signed-in app, it is shorter than drawn, and it never takes a
 * tap: the buttons under it work from the first frame. */
export function Intro() {
  const [show, setShow] = useState(() => {
    try {
      if (sessionStorage.getItem("damga-app-intro") === "1") return false;
      sessionStorage.setItem("damga-app-intro", "1");
    } catch {
      return false;
    }
    return true;
  });
  useEffect(() => {
    if (!show) return;
    const id = setTimeout(() => setShow(false), 1600);
    return () => clearTimeout(id);
  }, [show]);
  if (!show) return null;
  return (
    <div className="intro" aria-hidden="true">
      <div className="intro-mark">
        <span className="intro-ring" />
        <span className="mark" />
      </div>
      <span className="intro-word">Damga</span>
    </div>
  );
}

/* A date a person would say out loud. */
export function onDate(iso: string, withYear = false): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: withYear ? "short" : "long",
    ...(withYear ? { year: "numeric" } : {}),
  });
}

/* A Qatari number the way it is read: +974 5568 4747. Anything else as sent. */
export function phoneForDisplay(e164: string): string {
  const m = /^\+974(\d{4})(\d{4})$/.exec(e164);
  return m ? `+974 ${m[1]} ${m[2]}` : e164;
}

export function monthYear(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { month: "short", year: "numeric" });
}
